import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { headerStyle, modalStyle, inputStyle } from "../OrderEntry/styles";
import { tokens, withAlpha } from "../../Common/designTokens";
import {
  MA_INVENTORY_FILTER_FIELDS,
  buildDefaultMAInventoryFilters,
} from "../../Store/maInventoryStore";

// Seeds the local draft from the applied filters, backfilling any missing range
// with the empty default so every field is always a controlled { min, max }.
const seedDraftFromFilters = (filters) => {
  const base = buildDefaultMAInventoryFilters();
  MA_INVENTORY_FILTER_FIELDS.forEach(({ key }) => {
    const range = filters?.[key] || {};
    base[key] = {
      min: range.min ?? "",
      max: range.max ?? "",
    };
  });
  return base;
};

// ---------------------------------------------------------------------------
// FILTER INPUT RULES (applied to both the Min and Max box of each field)
//
// | Field       | Key             | Type    | Decimals | Min  | Max           | Neg |
// |-------------|-----------------|---------|----------|------|---------------|-----|
// | Bid Yield   | bidYield        | Decimal | up to 2  | -100 | 100           | Yes |
// | Bid Qty     | bidQuantity     | Integer | 0 (none) | 0    | 1,000,000,000 | No  |
// | Bid Px      | bidPrice        | Decimal | up to 2  | 0    | 200           | No  |
// | Offer Qty   | offerQuantity   | Integer | 0 (none) | 0    | 1,000,000,000 | No  |
// | Offer Px    | offerPrice      | Decimal | up to 2  | 0    | 200           | No  |
// | Offer Yield | offerYield      | Decimal | up to 2  | -100 | 100           | Yes |
//
// By category:
//   - Quantities (Bid/Offer Qty): whole numbers only, 0 .. 1,000,000,000, no
//     decimal point, no negatives.
//   - Prices (Bid/Offer Px): up to 2 decimals, 0 .. 200, no negatives.
//   - Yields (Bid/Offer Yield): up to 2 decimals, -100 .. 100, negatives allowed.
//
// Rules enforced on every field (Min and Max):
//   - Out-of-range typing is blocked live — a value below the field's min or
//     above its max is rejected keystroke-by-keystroke (see handleRangeChange).
//   - Blocked keys: "e", "E", "+" always; "-" blocked except on yield fields;
//     "." additionally blocked on quantity fields (see the blockNon*Keys handlers).
//   - On blur: a lone "-", ".", or "-." clears to empty; "-0" (and "-0.0", etc.)
//     normalizes to "0" (see normalizeRangeValueOnBlur).
//   - Min <= Max within a single field, else a red "Min cannot be greater than
//     Max" error is shown (see the `errors` memo).
//   - An empty Min or Max means that side is unbounded.
//   - Apply Filters is enabled only when there are no errors, no incomplete
//     entries (e.g. a stray "-"), and the draft differs from the applied filters.
//   - Price/yield precision is capped at 2 decimals to match the grid's display
//     and the store's compare precision (MA_INVENTORY_FILTER_COMPARE_DECIMALS).
//
// The constants below are the single source of truth for these limits; keep the
// table in sync when changing them.
// ---------------------------------------------------------------------------

// Per-field upper bounds for the filter inputs. Qty is capped at 1,000,000,000
// (whole numbers only — see MA_FILTER_QUANTITY_FIELDS); price is intentionally
// capped at 200 HERE ONLY — higher than Order Entry's 150 — since this is a
// filter range, not an order price. Yields (a percentage) are capped at 100.
const MA_FILTER_FIELD_LIMITS = {
  bidQuantity: 1000000000,
  offerQuantity: 1000000000,
  bidPrice: 200,
  offerPrice: 200,
  bidYield: 100,
  offerYield: 100,
};

// Per-field lower bounds. Every field is non-negative (0) EXCEPT the yield
// fields, which accept negative yields down to -100.
const MA_FILTER_FIELD_MIN_LIMITS = {
  bidYield: -100,
  offerYield: -100,
};
const getFieldMinLimit = (field) => MA_FILTER_FIELD_MIN_LIMITS[field] ?? 0;

// Price fields accept at most this many decimal places, matching the grid's
// 2-decimal price display and the filter's 2-decimal comparison precision
// (see MA_INVENTORY_FILTER_COMPARE_DECIMALS in maInventoryStore).
const MA_FILTER_PRICE_FIELDS = new Set(["bidPrice", "offerPrice"]);
const MA_FILTER_PRICE_MAX_DECIMALS = 2;

// Quantity fields accept whole numbers only — no decimal point at all — since a
// bid/offer quantity is never fractional.
const MA_FILTER_QUANTITY_FIELDS = new Set(["bidQuantity", "offerQuantity"]);

// Yield fields accept a signed value in [-100, 100] with up to 2 decimals,
// matching the grid's 2-decimal yield display and the filter's 2-decimal
// comparison precision (see MA_INVENTORY_FILTER_COMPARE_DECIMALS in maInventoryStore).
const MA_FILTER_YIELD_FIELDS = new Set(["bidYield", "offerYield"]);
const MA_FILTER_YIELD_MAX_DECIMALS = 2;

// Only non-negative numbers: digits with an optional single decimal point (no
// letters, no sign, no exponent). Empty string is allowed so fields can clear.
const NON_NEGATIVE_NUMBER_PATTERN = /^\d*\.?\d*$/;
// Signed variant for yield fields: an optional leading minus, then the same
// digits/decimal shape. A lone "-" is allowed as intermediate typing (it parses
// as "unbounded", like an empty field).
const SIGNED_NUMBER_PATTERN = /^-?\d*\.?\d*$/;

// Block characters our validation would reject anyway, at the keystroke, so they
// never flash in the field: exponent/sign chars. Yield fields allow "-" so
// negative yields can be entered.
const BLOCKED_NUMBER_KEYS = ["e", "E", "+", "-"];
const BLOCKED_NUMBER_KEYS_ALLOW_SIGN = ["e", "E", "+"];
const blockNonNumericKeys = (event) => {
  if (BLOCKED_NUMBER_KEYS.includes(event.key)) {
    event.preventDefault();
  }
};
const blockNonNumericKeysAllowSign = (event) => {
  if (BLOCKED_NUMBER_KEYS_ALLOW_SIGN.includes(event.key)) {
    event.preventDefault();
  }
};
// Integer fields (quantities) additionally block "." so no decimals can be typed.
const BLOCKED_NUMBER_KEYS_INTEGER = ["e", "E", "+", "-", "."];
const blockNonIntegerKeys = (event) => {
  if (BLOCKED_NUMBER_KEYS_INTEGER.includes(event.key)) {
    event.preventDefault();
  }
};

// Cleans up a field's value when it loses focus. Intermediate/incomplete
// entries that don't parse to a finite number — a lone "-" (allowed while
// typing a negative yield), a lone ".", or "-." — collapse to empty. Any value
// that equals zero, including "-0" / "-0.0", is normalized to "0" so a stray
// minus sign never lingers on a zero.
const normalizeRangeValueOnBlur = (value) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") return "";
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return "";
  if (num === 0) return "0";
  return trimmed;
};

// Numeric range input styling. The inputs are type="text" (with inputMode
// "decimal"), NOT type="number": a controlled type="number" input hides invalid
// intermediate buffers like a lone "-" behind an empty .value, so React can't
// clear the stray character on blur. type="text" lets the controlled value hold
// and clear those entries faithfully; our keydown blockers + pattern validation
// keep input numeric. The filled input's vertical padding is trimmed to give a
// shorter field.
const numberFieldStyle = {
  ...inputStyle,
  flex: 1,
  // Compact height without the label/value collision. The global theme forces
  // `padding: 14px !important` on inputs, so we override with !important + a
  // higher-specificity selector. The top padding must clear the shrunk floating
  // label (which sits ~7px from the top), so we keep enough headroom and trim
  // only the bottom to reduce overall height.
  "& .MuiInputBase-root.MuiFilledInput-root": {
    paddingTop: 0,
  },
  "& .MuiFilledInput-input": {
    padding: "22px 12px 6px !important",
    fontSize: "14px",
  },
};

// A single "field" row: label + Min/Max numeric inputs, with an inline error
// shown when Min exceeds Max.
const RANGE_LABEL_WIDTH = 86; // fixed so all row labels align and inputs line up (wide enough to keep "Offer Yield:" on one line)

// Barely-there hairline that visually separates the ranges without drawing attention.
const RANGE_SEPARATOR_COLOR = withAlpha(tokens.colors.bodyBorderColor, 0.25);

const RangeFilterRow = ({ label, range, error, onChange, isLast = false, allowNegative = false, integerOnly = false }) => {
  const handleKeyDown = integerOnly
    ? blockNonIntegerKeys
    : allowNegative
      ? blockNonNumericKeysAllowSign
      : blockNonNumericKeys;
  // Whole-number fields get the numeric (no-decimal) mobile keypad; the rest
  // allow a decimal point.
  const inputMode = integerOnly ? "numeric" : "decimal";
  return (
  <Grid
    item
    xs={12}
    sx={{
      pb: isLast ? 0 : 2,
      borderBottom: isLast ? "none" : `1px solid ${RANGE_SEPARATOR_COLOR}`,
    }}
  >
    {/* Label sits in a fixed, left-aligned column so the Min fields stay
        vertically aligned across rows. The label→fields gap matches the popup's
        left/right padding (px: 3) so the row reads symmetrically. */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
      <Typography
        sx={{
          width: RANGE_LABEL_WIDTH,
          flexShrink: 0,
          textAlign: "left",
          whiteSpace: "nowrap",
          fontWeight: 500,
          fontSize: "16px",
          color: tokens.colors.fontColour,
        }}
      >
        {label}:
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
        <TextField
          fullWidth
          type="text"
          variant="filled"
          label="Min"
          value={range.min}
          onChange={(event) => onChange("min", event.target.value)}
          onBlur={(event) => onChange("min", normalizeRangeValueOnBlur(event.target.value))}
          onKeyDown={handleKeyDown}
          error={Boolean(error)}
          sx={numberFieldStyle}
          InputProps={{ disableUnderline: true }}
          inputProps={{ inputMode }}
        />
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "14px",
            color: tokens.colors.fontColour85,
            flexShrink: 0,
          }}
        >
          To
        </Typography>
        <TextField
          fullWidth
          type="text"
          variant="filled"
          label="Max"
          value={range.max}
          onChange={(event) => onChange("max", event.target.value)}
          onBlur={(event) => onChange("max", normalizeRangeValueOnBlur(event.target.value))}
          onKeyDown={handleKeyDown}
          error={Boolean(error)}
          sx={numberFieldStyle}
          InputProps={{ disableUnderline: true }}
          inputProps={{ inputMode }}
        />
      </Box>
    </Box>
    {error ? (
      <Typography
        sx={{
          mt: 0.5,
          ml: `${RANGE_LABEL_WIDTH + 24}px`,
          fontSize: "12px",
          color: tokens.palette.danger,
        }}
      >
        {error}
      </Typography>
    ) : null}
  </Grid>
  );
};

const MAInventoryFilterModal = ({
  open,
  onClose,
  onApplyFilters,
  initialFilters = {},
}) => {
  const [draft, setDraft] = useState(() => seedDraftFromFilters(initialFilters));

  // Re-seed the draft each time the modal opens so it always reflects the
  // currently applied filters (and discards any un-applied edits from before).
  useEffect(() => {
    if (open) {
      setDraft(seedDraftFromFilters(initialFilters));
    }
  }, [open, initialFilters]);

  const handleRangeChange = useCallback((field, bound, value) => {
    // Reject invalid keystrokes up front (like the Order Entry popup): no
    // letters and no values outside the field's [min, max] bounds. Price fields
    // are capped to 2 decimals; yield fields accept a signed value in [-100, 100]
    // with up to 2 decimals; every other field is non-negative.
    const isYield = MA_FILTER_YIELD_FIELDS.has(field);
    if (value !== "") {
      const pattern = isYield ? SIGNED_NUMBER_PATTERN : NON_NEGATIVE_NUMBER_PATTERN;
      if (!pattern.test(value)) return;

      // Quantities are whole numbers only — reject anything with a decimal point
      // (covers pastes, since the keydown blocker already stops typing ".").
      if (MA_FILTER_QUANTITY_FIELDS.has(field) && value.includes(".")) return;

      if (MA_FILTER_PRICE_FIELDS.has(field)) {
        const decimals = value.split(".")[1];
        if (decimals && decimals.length > MA_FILTER_PRICE_MAX_DECIMALS) return;
      }

      if (isYield) {
        const decimals = value.split(".")[1];
        if (decimals && decimals.length > MA_FILTER_YIELD_MAX_DECIMALS) return;
      }

      const num = Number(value);
      if (Number.isFinite(num)) {
        const limit = MA_FILTER_FIELD_LIMITS[field];
        if (num < getFieldMinLimit(field)) return;
        if (limit !== undefined && num > limit) return;
      }
    }

    setDraft((prev) => ({
      ...prev,
      [field]: { ...prev[field], [bound]: value },
    }));
  }, []);

  // Validation gates the Apply button: a bound may not fall below the field's
  // minimum or exceed its limit, and Min may not exceed Max within a single range.
  const errors = useMemo(() => {
    const result = {};
    MA_INVENTORY_FILTER_FIELDS.forEach(({ key }) => {
      const { min, max } = draft[key] || {};
      const minNum = min === "" ? null : Number(min);
      const maxNum = max === "" ? null : Number(max);
      const limit = MA_FILTER_FIELD_LIMITS[key];
      const minLimit = getFieldMinLimit(key);
      const isBelowMin = (num) =>
        num !== null && Number.isFinite(num) && num < minLimit;
      const isOverLimit = (num) =>
        limit !== undefined && num !== null && Number.isFinite(num) && num > limit;

      if (isBelowMin(minNum) || isBelowMin(maxNum)) {
        result[key] = minLimit < 0
          ? `Value cannot be less than ${minLimit}`
          : "Value cannot be negative";
      } else if (isOverLimit(minNum) || isOverLimit(maxNum)) {
        // Pinned to en-US so the grouping is the same for every user. A bare
        // toLocaleString() follows the host locale, which would render the qty
        // cap as "1,00,00,00,000" (en-IN) or "1.000.000.000" (de-DE).
        result[key] = `Value cannot exceed ${limit.toLocaleString("en-US")}`;
      } else if (
        minNum !== null &&
        maxNum !== null &&
        Number.isFinite(minNum) &&
        Number.isFinite(maxNum) &&
        minNum > maxNum
      ) {
        result[key] = "Min cannot be greater than Max";
      }
    });
    return result;
  }, [draft]);

  const hasErrors = Object.keys(errors).length > 0;

  // A bound that is non-empty but doesn't parse to a finite number is an
  // incomplete entry (e.g. a lone "-", ".", or "-." while typing a negative
  // yield). It shouldn't surface a red error — it's cleared on blur — but Apply
  // must stay disabled until every bound is either empty or a real number.
  const hasIncompleteEntry = useMemo(() => {
    const isIncomplete = (value) =>
      value !== "" && !Number.isFinite(Number(value));
    return MA_INVENTORY_FILTER_FIELDS.some(({ key }) => {
      const { min, max } = draft[key] || {};
      return isIncomplete(min) || isIncomplete(max);
    });
  }, [draft]);

  // Apply stays disabled until the draft actually differs from the currently
  // applied filters, so there is nothing to apply on open (or after reverting).
  const hasChanges = useMemo(() => {
    const norm = (value) => String(value ?? "").trim();
    return MA_INVENTORY_FILTER_FIELDS.some(({ key }) => {
      const draftRange = draft[key] || {};
      const appliedRange = initialFilters?.[key] || {};
      return (
        norm(draftRange.min) !== norm(appliedRange.min)
        || norm(draftRange.max) !== norm(appliedRange.max)
      );
    });
  }, [draft, initialFilters]);

  const isApplyDisabled = hasErrors || hasIncompleteEntry || !hasChanges;

  const handleApply = useCallback(() => {
    if (hasErrors) return;
    // Normalize bounds to trimmed strings; the store's filter helpers treat an
    // empty string as "unbounded".
    const normalized = buildDefaultMAInventoryFilters();
    MA_INVENTORY_FILTER_FIELDS.forEach(({ key }) => {
      const { min, max } = draft[key] || {};
      normalized[key] = {
        min: String(min ?? "").trim(),
        max: String(max ?? "").trim(),
      };
    });
    onApplyFilters(normalized);
    onClose();
  }, [draft, hasErrors, onApplyFilters, onClose]);

  const handleClear = useCallback(() => {
    setDraft(buildDefaultMAInventoryFilters());
  }, []);

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableEscapeKeyDown={true}
      aria-labelledby="ma-inventory-filter-modal-title"
    >
      <Box
        sx={{
          ...modalStyle,
          width: "600px",
          maxWidth: "90vw",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ ...headerStyle, flexShrink: 0, px: 3 }}>
          <Typography
            id="ma-inventory-filter-modal-title"
            variant="h6"
            component="h2"
            sx={{ fontWeight: "bold" }}
          >
            Filter Quotes
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2 }}>
          <Grid container spacing={2}>
            {MA_INVENTORY_FILTER_FIELDS.map(({ key, label }, index) => (
              <RangeFilterRow
                key={key}
                label={label}
                range={draft[key] || { min: "", max: "" }}
                error={errors[key]}
                onChange={(bound, value) => handleRangeChange(key, bound, value)}
                isLast={index === MA_INVENTORY_FILTER_FIELDS.length - 1}
                allowNegative={MA_FILTER_YIELD_FIELDS.has(key)}
                integerOnly={MA_FILTER_QUANTITY_FIELDS.has(key)}
              />
            ))}
          </Grid>
        </Box>

        {/* Footer — the header's close (X) dismisses the modal, so there is no
            separate Cancel here. Clear All (secondary) sits just left of the
            primary Apply Filters, both right-aligned. */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2,
            borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
            backgroundColor: tokens.colors.defaultGridRowColor,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{
              textTransform: "none",
              fontWeight: "700",
              px: 3,
              py: 1,
              borderRadius: "8px",
            }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={isApplyDisabled}
            sx={{
              textTransform: "none",
              fontWeight: "700",
              px: 3,
              py: 1,
              borderRadius: "8px",
              backgroundColor: tokens.palette.primary,
              color: tokens.colors.white,
              "&:hover": { backgroundColor: tokens.palette.primaryA80 },
              // Explicit disabled visual: faded primary + not-allowed cursor,
              // otherwise the solid primary bg above makes it look enabled.
              "&.Mui-disabled": {
                backgroundColor: withAlpha(tokens.palette.primary, 0.45),
                color: tokens.colors.white,
                opacity: 1,
                cursor: "not-allowed",
              },
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default MAInventoryFilterModal;
